/**
 * RDKit.js MinimalLib loader and preview rendering (spec §8.9 item 1).
 * Verified against @rdkit/rdkit 2025.3.4: `initRDKitModule({locateFile})` is a global set by the
 * UMD script; `get_mol(molblock)` → `JSMol` with `get_svg(w, h)`. There is no reaction API in the
 * minimal build, so RXN files are split into their component molblocks (ADR 0008). `get_smiles()`
 * (canonical SMILES) verified against the same version's `index.d.ts`.
 */
import wasmUrl from "@rdkit/rdkit/dist/RDKit_minimal.wasm?url";
import jsUrl from "@rdkit/rdkit/dist/RDKit_minimal.js?url";
import type { RDKitModule } from "@rdkit/rdkit";

let modPromise: Promise<RDKitModule> | null = null;

export function loadRDKit(): Promise<RDKitModule> {
  if (!modPromise) {
    modPromise = new Promise<RDKitModule>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = jsUrl;
      s.onload = () => window.initRDKitModule({ locateFile: () => wasmUrl }).then(resolve, reject);
      s.onerror = () => reject(new Error("failed to load RDKit.js"));
      document.head.appendChild(s);
    });
  }
  return modPromise;
}

export function moleculeSvg(rdkit: RDKitModule, molblock: string, w = 300, h = 200): string | null {
  const mol = rdkit.get_mol(molblock);
  if (!mol) return null;
  try {
    return mol.get_svg(w, h);
  } finally {
    mol.delete();
  }
}

export interface RxnParts {
  reactants: string[];
  products: string[];
  /** species drawn over the arrow (the RXN agent block) */
  agents: string[];
}

/** V2000 counts line: `nr np` or `nr np na`, fixed-width `%3d` fields. */
const V2000_COUNTS = /^\s*(\d+)\s+(\d+)(?:\s+(\d+))?\s*$/;

/**
 * Split an RXN (V2000 `$MOL` blocks or V3000 REACTANT/PRODUCT/AGENT sections) into its component
 * molblocks. Spec §8.9.2: the counts line may carry an agent count, and agents are never dropped.
 */
export function splitRxn(rxn: string): RxnParts {
  if (/^\$RXN V3000/m.test(rxn) || rxn.includes("M  V30 BEGIN REACTANT")) return splitRxnV3000(rxn);
  if (rxn.includes("$MOL")) {
    const parts = rxn.split("$MOL").map((p) => p.replace(/^\r?\n/, ""));
    const header = (parts[0] ?? "").split(/\r?\n/);
    // the counts line is line 5 of the header; fall back to the first line that looks like one
    const line = V2000_COUNTS.test(header[4] ?? "") ? header[4]! : (header.slice(1).find((l) => V2000_COUNTS.test(l)) ?? "0 0");
    const m = V2000_COUNTS.exec(line)!;
    const nr = Number(m[1]);
    const np = Number(m[2]);
    const mols = parts.slice(1).map((mol) => mol.trimEnd() + "\n");
    return { reactants: mols.slice(0, nr), products: mols.slice(nr, nr + np), agents: mols.slice(nr + np) };
  }
  return { reactants: [], products: [], agents: [] };
}

const V3000_SECTION: Record<string, keyof RxnParts> = { REACTANT: "reactants", PRODUCT: "products", AGENT: "agents" };

/** V3000 RXN: each `M  V30 BEGIN CTAB … END CTAB` becomes a standalone V3000 molblock. */
function splitRxnV3000(rxn: string): RxnParts {
  const out: RxnParts = { reactants: [], products: [], agents: [] };
  let section: keyof RxnParts | null = null;
  let ctab: string[] | null = null;
  for (const line of rxn.split(/\r?\n/)) {
    const begin = /^M {2}V30 BEGIN (REACTANT|PRODUCT|AGENT)\b/.exec(line);
    if (begin) section = V3000_SECTION[begin[1]!]!;
    else if (/^M {2}V30 END (REACTANT|PRODUCT|AGENT)\b/.test(line)) section = null;
    else if (/^M {2}V30 BEGIN CTAB/.test(line)) ctab = [line];
    else if (/^M {2}V30 END CTAB/.test(line) && ctab) {
      ctab.push(line);
      if (section) out[section].push(`\n  KSS\n\n  0  0  0  0  0  0  0  0  0  0999 V3000\n${ctab.join("\n")}\nM  END\n`);
      ctab = null;
    } else if (ctab) ctab.push(line);
  }
  return out;
}

export interface ComponentGroup {
  /** canonical SMILES from RDKit, the key of `body.coefficients` (§7.4.9 rule 3); "" if unparsable */
  smiles: string;
  molblock: string;
  /** drawn copies */
  count: number;
}

/** Collapse identical components (equal canonical SMILES) into one entry with a count (§8.9.2). */
export function groupComponents(molblocks: string[], smilesOf: (molblock: string) => string | null): ComponentGroup[] {
  const out = new Map<string, ComponentGroup>();
  for (const molblock of molblocks) {
    const smiles = smilesOf(molblock) ?? "";
    // an unparsable component never merges with another one
    const key = smiles || `raw:${out.size}`;
    const g = out.get(key);
    if (g) g.count += 1;
    else out.set(key, { smiles, molblock, count: 1 });
  }
  return [...out.values()];
}

export function canonicalSmiles(rdkit: RDKitModule, molblock: string): string | null {
  const mol = rdkit.get_mol(molblock);
  if (!mol) return null;
  try {
    return mol.is_valid() ? mol.get_smiles() : null;
  } finally {
    mol.delete();
  }
}

/** The coefficient shown for a group: an authored override wins over the drawn count. */
export function displayCoefficient(group: ComponentGroup, overrides: Record<string, number>): number {
  const o = group.smiles ? overrides[group.smiles] : undefined;
  return typeof o === "number" && o > 0 ? o : group.count;
}

export function reactionSvgs(rdkit: RDKitModule, rxn: string): { reactants: string[]; products: string[] } {
  const { reactants, products } = splitRxn(rxn);
  const conv = (m: string) => moleculeSvg(rdkit, m, 180, 140) ?? "";
  return { reactants: reactants.map(conv).filter(Boolean), products: products.map(conv).filter(Boolean) };
}
